import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, concatMap, from, map, Observable, of, toArray } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AgentTokenService } from '../../services/agent-token.service';
import { CustomerUserDTO } from '../../models/customer.model';
import { CreatePatRequest } from '../../models/pat.model';
import { PatCreateDialogComponent, PatScopesSource } from '../../account/pat-create-dialog/pat-create-dialog.component';
import { AGENT_ENV_FILE_NAME, AgentEnvEntry, buildAgentEnvFile } from '../../shared/agent-env-file';
import { saveBlob } from '../../shared/file-download';

/** Name of every token created by the bulk action (decision of 15.10.2026, NEX-1980). */
export const AGENT_BULK_TOKEN_NAME = 'ki-team';

export interface AgentBulkFailure {
  kuerzel: string;
  reasonKey: string;
}

export interface AgentBulkResult {
  created: AgentEnvEntry[];
  failed: AgentBulkFailure[];
}

type Outcome = { created: AgentEnvEntry } | { failed: AgentBulkFailure };

/**
 * Creates a `ki-team` token for each selected agent account and offers the values as `zugaenge.env`.
 * The values live only in this component and are dropped when the result is closed.
 */
@Component({
  selector: 'app-agent-tokens-bulk',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, Modal, ModalEscapeGuardDirective, MatButton, MatIcon, PatCreateDialogComponent],
  templateUrl: './agent-tokens-bulk.component.html',
  styleUrl: './agent-tokens-bulk.component.scss',
})
export class AgentTokensBulkComponent {
  readonly customerId = input.required<string>();
  /** Selected active agent accounts with kuerzel, in table order. */
  readonly agents = input.required<CustomerUserDTO[]>();

  readonly cancelled = output<void>();
  readonly finished = output<void>();

  private readonly agentTokenService = inject(AgentTokenService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tokenName = AGENT_BULK_TOKEN_NAME;
  readonly creating = signal(false);
  readonly result = signal<AgentBulkResult | null>(null);
  readonly closed = signal(false);
  readonly downloaded = signal(false);
  readonly confirmingDiscard = signal(false);

  readonly scopesSource: PatScopesSource = () => this.agentTokenService.getTokenScopes(this.customerId());

  onCreate(request: CreatePatRequest): void {
    const expiresAt = request.expiresAt;
    if (!expiresAt || this.creating()) return;
    this.creating.set(true);
    from(this.agents())
      .pipe(
        concatMap((agent) => this.createFor(agent, request.scopes, expiresAt)),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((outcomes) => {
        this.result.set({
          created: outcomes.flatMap((outcome) => ('created' in outcome ? [outcome.created] : [])),
          failed: outcomes.flatMap((outcome) => ('failed' in outcome ? [outcome.failed] : [])),
        });
        this.creating.set(false);
      });
  }

  /** While tokens are being created the result still follows, so closing the dialog cancels nothing. */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible && !this.creating() && !this.result() && !this.closed()) this.cancelled.emit();
  }

  download(): void {
    const result = this.result();
    if (!result || result.created.length === 0) return;
    const content = buildAgentEnvFile(result.created);
    saveBlob(new Blob([content], { type: 'text/plain;charset=utf-8' }), AGENT_ENV_FILE_NAME);
    this.downloaded.set(true);
  }

  /** A click beside the result or on X must not silently drop values that were never downloaded. */
  requestClose(): void {
    const hasUnsavedValues = (this.result()?.created.length ?? 0) > 0 && !this.downloaded();
    if (hasUnsavedValues) this.confirmingDiscard.set(true);
    else this.close();
  }

  close(): void {
    this.confirmingDiscard.set(false);
    this.result.set(null);
    this.closed.set(true);
    this.finished.emit();
  }

  private createFor(agent: CustomerUserDTO, scopes: string[], expiresAt: string): Observable<Outcome> {
    const kuerzel = agent.kuerzel ?? '';
    return this.agentTokenService
      .createToken(this.customerId(), agent.id, { name: AGENT_BULK_TOKEN_NAME, scopes, expiresAt })
      .pipe(
        map((response): Outcome => ({ created: { kuerzel, token: response.token } })),
        catchError((error: unknown) =>
          of<Outcome>({
            failed: {
              kuerzel,
              reasonKey:
                error instanceof HttpErrorResponse && error.status === 409
                  ? 'agentTokens.bulk.reasonExists'
                  : 'agentTokens.bulk.reasonError',
            },
          }),
        ),
      );
  }
}
